import React, { useEffect, useMemo, useState } from "react";
import useSummary from "../../../useSummary";
import { statusCategories } from "../../../../Findings/FindingDetailsPane/FindingDetailsPaneHeader/Statuses/StatusUtils";
import {
  DUE_DATE_STATUS,
  TICKET_SLA,
  type TicketSlaStatusKey,
} from "../../SLA/SLAUtils";
import {
  createColumnHelper,
  type CellContext,
  type ColumnDef,
} from "@tanstack/react-table";
import {
  TableCell,
  tableCellTypesEnum,
  formatNumber,
} from "@SeemplicityDev/shared";
import FindingsBySeverityChart from "../FindingsBySeverity/FindingsBySeverityChart";
import TableChart, { type TableChartRowData } from "../TableChart";
import FindingsBySeverityLegend from "../FindingsBySeverity/FindingsSeverityLegend";
import "./DueDateBySeverity.scss";
import useSetDataWithExport from "../useSetDataWithExport";
import useWidgetFilterNavigator from "../../../useWidgetFilterNavigator";
import useDebounceLoading from "../useDebounceLoading";
import type { CountBySeverity } from "../TopFeatureTables/TopFeatureTable";
import type { SelectedScope } from "../../../../types/scope";

export interface DueDateBySeverityTableProps {
  selectedScope?: SelectedScope | null;
  filterToExecute: string | null;
  scopeToExecute: string | null;
  id: string;
  isForExport?: boolean;
  setTotalWidgets?: ((total: number) => void) | null;
  index?: number | null;
  setIsReadyToExport?: ((ready: boolean) => void) | null;
}

interface DueDateBySeverityRow {
  dueDate: TicketSlaStatusKey;
  filterFieldValue: TicketSlaStatusKey;
  findings: CountBySeverity;
}

// Raw summary rows arrive as [dueDateStatus, severity, count] tuples from the
// GraphQL summary query (typed as unknown at the useSummary boundary).
type SummaryRow = [TicketSlaStatusKey, string, number];

const DueDateBySeverityTable: React.FC<DueDateBySeverityTableProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  id,
  isForExport = false,
  setTotalWidgets = null,
  index = null,
  setIsReadyToExport = null,
}) => {
  const [tableData, setTableData] = useState<DueDateBySeverityRow[] | null>(
    null
  );
  const [maxFindings, setMaxFindings] = useState(0);

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: scopeToExecute,
    scopeId: (selectedScope?.value?.id ?? null) as string | null,
  });

  const {
    summaryData: data,
    summaryDataError,
    summaryLoading,
    refetchSummary,
  } = useSummary({
    variables: {
      name: "FINDINGS_VIEW",
      selection: ["finding.id"],
      group_by: [`ticket.ticket_due_date_status`, `finding.severity`],
      aggregate_functions: ["COUNT"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute || null,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
        filters_fields_to_override: [
          JSON?.stringify({
            field: "remediation_item.actual_status",
            condition: "",
            value: {
              field: "category",
              condition: "in",
              value: [statusCategories.OPEN],
            },
          }),
        ],
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const { setDataWithExport, setLoadedRowsCount } =
    useSetDataWithExport<DueDateBySeverityRow>({
      isForExport,
      index,
      setTotalWidgets,
      setData: setTableData,
      data: tableData,
      error: summaryDataError,
      loading: summaryLoading,
      setIsReadyToExport,
    });

  const isLoading = useDebounceLoading([summaryLoading]);

  useEffect(() => {
    if (!!data) {
      const dataArray = (data as SummaryRow[]).reduce<DueDateBySeverityRow[]>(
        (acc, curr) => {
          if (curr[0] === DUE_DATE_STATUS) {
            return acc;
          }

          if (!acc?.find((item) => item.dueDate === curr[0])) {
            const dataObj: DueDateBySeverityRow = {
              dueDate: curr[0],
              filterFieldValue: curr[0],
              findings: {
                total: curr[2],
              },
            };
            dataObj.findings[curr[1]] = curr[2];
            acc?.push(dataObj);
          } else {
            const dataObj = acc.find((item) => item.dueDate === curr[0]);
            if (dataObj) {
              dataObj.findings[curr[1]] = curr[2];
              dataObj.findings.total += curr[2];
            }
          }

          return acc;
        },
        []
      );
      const max = Math.max(...dataArray?.map((ds) => ds.findings?.total));
      const sortedData = dataArray.sort(
        (a, b) => b.findings.total - a.findings.total
      );

      setMaxFindings(max);
      setDataWithExport(sortedData);
    }
  }, [data]);

  const chartClick = (severity?: string, dueDateStatus?: unknown[]) => {
    onFilterClick([
      [
        "in",
        "remediation_item.ticket_due_date_status",
        dueDateStatus as unknown as string[],
      ],
      ["in", "remediation_item.severity", severity as string],
    ]);
  };

  const columnHelper = createColumnHelper<DueDateBySeverityRow>();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("dueDate", {
        cell: (
          props: CellContext<DueDateBySeverityRow, TicketSlaStatusKey>
        ) => {
          const dueDateTitle = TICKET_SLA[props.getValue()].title;
          return (
            <TableCell>
              <div className={"due-date-name-wrapper"}>
                <span className={"due-date-status"}>{dueDateTitle}</span>
              </div>
            </TableCell>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
        header: "Due Date Status",
        minSize: 50,
      }),
      columnHelper.accessor("findings", {
        cell: (props: CellContext<DueDateBySeverityRow, CountBySeverity>) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <div
              className={`findings-wrapper ${isForExport ? "exported" : ""}`}
            >
              <span
                className={"findings-count"}
                title={formatNumber(props.getValue()?.total) || undefined}
              >
                {formatNumber(props.getValue()?.total)}
              </span>
              <FindingsBySeverityChart
                loading={summaryLoading}
                error={summaryDataError}
                findingCounts={props.getValue()}
                namePrefix={props.row?.original?.dueDate}
                filterFieldValue={props.row?.original?.filterFieldValue}
                maxFindings={maxFindings}
                widgetId={id}
                onChartClick={chartClick}
                isForExport={isForExport}
                setLoaded={() => setLoadedRowsCount((prev) => prev + 1)}
              />
            </div>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Findings",
        // Runtime preserves the original "80%" string sizing; tanstack types
        // `size` as a number, so this cast keeps behavior unchanged.
        size: "80%" as unknown as number,
      }),
    ];
  }, [tableData]);

  return (
    <TableChart
      className={"findings-by-sla-and-severity-table"}
      columns={columns as unknown as ColumnDef<TableChartRowData, unknown>[]}
      tableData={tableData as unknown as TableChartRowData[] | null}
      isLoading={isLoading}
      error={summaryDataError}
      onRefetch={refetchSummary}
      legend={<FindingsBySeverityLegend />}
      isForExport={isForExport}
    />
  );
};

export default DueDateBySeverityTable;
